import { z } from 'zod';
import { apiGet, apiSend } from './api';

/**
 * KREATOR WDROŻENIA SMART SYNCA — odczyt stanu i dwa odhaczenia człowieka.
 *
 * ⚠️ EKRAN NICZEGO TU NIE LICZY. Stan siedmiu kroków przychodzi gotowy z instancji
 * (`api/src/wdrozenie/kroki-wdrozenia.ts`), bo kolejność kroków jest treścią poprawności, a nie
 * układem strony. Odtwarzanie tych reguł we froncie znaczyłoby dwie kopie, które rozjadą się przy
 * pierwszej zmianie — dokładnie tak, jak rozjechał się baner rozjazdu z nazwą przycisku (29.08.2026).
 */

export const KROKI = [
  'PRZYGOTOWANIE',
  'AGENCI',
  'TRYB',
  'KLUCZ',
  'BAZA_DO_CHMURY',
  'MASTER',
  'TEST_I_ROZSYLANIE',
] as const;
export type IdKroku = (typeof KROKI)[number];

const SprawdzenieSchema = z.object({
  nazwa: z.string(),
  ok: z.boolean(),
  szczegol: z.string(),
  /**
   * ⚠️⚠️ CZY TO SPRAWDZENIE TRZYMA ZAMEK OSTATNIEGO KROKU — dołożone 09.09.2026.
   * `undefined` (starsze API) czytamy jako „blokuje": tak było przed tą zmianą, a zgadywanie
   * w drugą stronę zapaliłoby przycisk włączający rozsyłanie na podstawie braku pola.
   */
  blokuje: z.boolean().optional(),
});

const KrokSchema = z.object({
  id: z.enum(KROKI),
  stan: z.enum(['ZROBIONE', 'TERAZ', 'CZEKA', 'UWAGA']),
  powody: z.array(z.string()),
  /** Czy przycisk tego kroku wolno kliknąć — rozstrzyga instancja, nie ekran. */
  wolno: z.boolean(),
  sprawdzenia: z.array(SprawdzenieSchema).optional(),
});
export type KrokWdrozenia = z.infer<typeof KrokSchema>;

const WezelSchema = z.object({
  nodeId: z.string(),
  maAutomat: z.boolean(),
  potwierdzony: z.boolean(),
  odrzucony: z.boolean(),
  minutOdKontaktu: z.number().nullable(),
  synchronizuje: z.boolean(),
  bazaLokalna: z.string().nullable(),
  wersja: z.string().nullable(),
  problem: z.string().nullable(),
});
export type WezelWdrozenia = z.infer<typeof WezelSchema>;

const StanSchema = z.object({
  wyspa: z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    tryb: z.enum(['ZWYKLY', 'SMART_SYNC']),
    masterServer: z.string().nullable(),
    masterDatabase: z.string().nullable(),
    masterConfirmed: z.boolean(),
    masterNodeId: z.string().nullable(),
    wydaniaRodzenstwa: z.boolean(),
    /**
     * GRANICA CZASU KANAŁU WYDAŃ RODZEŃSTWA — `null` znaczy „bez granicy".
     *
     * ⚠️ `nullish`, nie `nullable`: starsza instancja tego pola nie odda, a schemat ostrzejszy
     * od odpowiedzi zamienia UDANY odczyt w błąd ekranu.
     */
    wydaniaOdKiedy: z.string().nullish(),
    /**
     * OSTATNIE „ROZEŚLIJ ZALEGŁE WYDANIA OD" — `null`, gdy na tej wyspie nikt go nie zlecał.
     *
     * ⚠️⚠️ ZAMÓWIONE 29.09.2026: „jak raz roześlemy, opcja powinna po wdrożeniu dawać już tylko
     * info". Tylko przy `null` kreator pokazuje formularz; stan liczy instancja z kursorów węzłów.
     * `nullish` z tego samego powodu co granica wyżej — starsza instancja tego pola nie odda.
     */
    nadrobienie: z
      .object({
        od: z.string(),
        zlecono: z.string().nullable(),
        stan: z.enum(['W_TOKU', 'ZAKONCZONE', 'PRZERWANE']),
        wezlowWToku: z.number(),
      })
      .nullish(),
  }),
  kroki: z.array(KrokSchema),
  wezly: z.array(WezelSchema),
  odhaczenia: z.array(z.object({ krok: z.string(), at: z.string(), byEmail: z.string() })),
  rozjazdy: z.array(
    z.object({
      tabela: z.string(),
      vmid: z.number(),
      stabilny: z.boolean(),
      /**
       * ⚠️ `optional`, bo instancja starsza od 01.09.2026 tego pola nie oddaje. Brak = ekran nie
       * rozstrzyga rodzaju i mówi tylko o liczbach — a nie udaje, że wie, czy to rozjazd treści.
       */
      rodzaj: z.enum(['BRAK', 'LICZBY', 'TRESC']).optional(),
      /**
       * KTÓRE KOLUMNY SIĘ RÓŻNIĄ. Puste albo brak = portal jeszcze nie wie (agent starszy niż
       * 0.7.165 albo prośba o skróty poszła w tym cyklu) — ekran musi to mówić, a nie milczeć.
       */
      kolumny: z.array(z.string()).optional(),
      /** `true` = kolumny zgodne, a wiersze nie: te same wartości leżą w innych przegródkach. */
      innyUklad: z.boolean().optional(),
      wezly: z.array(z.object({ nodeId: z.string(), wierszy: z.number(), skrot: z.string().nullable().optional() })),
    }),
  ),
  /**
   * AUTOMATY WYSTĘPUJĄCE W ROZJEŹDZIE — nazwa i węzeł-właściciel.
   *
   * ⚠️ WŁAŚCICIEL NIE JEST TYM SAMYM CO NAJBOGATSZY WĘZEŁ: najbogatszy potrafi trzymać zjawę po
   * klonowaniu wyspy, a wtedy braki liczone od niego wskazują palcem maszynę, która ma rację.
   */
  automaty: z
    .array(z.object({ vmid: z.number(), nazwa: z.string().nullable(), wlascicielWezel: z.string().nullable() }))
    .optional(),
  /** VMID-y, przy których „Wyrównaj geometrię" naprawdę ma co zrobić (brak CAŁKOWITY, akcja `I`). */
  doWyrownania: z.array(z.number()).optional(),
  /** Tabele, które ten przycisk realnie rusza — czujnik liczy więcej, niż wyrównanie rozsyła. */
  tabeleWyrownania: z.array(z.string()).optional(),
  progMilczeniaMin: z.number(),
  /**
   * WĘZEŁ CHMUROWY WYSPY — `null`, gdy sidecara jeszcze nie ma.
   *
   * ⚠️ EKRAN NIE ROZPOZNAJE GO SAM. Szukanie po `machine === null` brało za sidecara maszynę świeżo
   * po rejestracji i dawało dwa sprzeczne zdania w sąsiednich krokach kreatora (01.09.2026).
   * `optional` tylko dla instancji starszej niż ta zmiana.
   */
  chmuraNodeId: z.string().nullable().optional(),
  wersjaDocelowa: z.string().nullable(),
});
export type StanWdrozenia = z.infer<typeof StanSchema>;

export const fetchWdrozenie = (groupId: string) => apiGet(`/islands/${groupId}/wdrozenie`, StanSchema);

/** `wlaczone: false` cofa odhaczenie — technik, który znajdzie kolejną maszynę, musi mieć jak wrócić. */
export const odhaczKrok = (groupId: string, krok: 'PRZYGOTOWANIE' | 'MASZYNY_KOMPLET', wlaczone: boolean) =>
  apiSend(
    'PUT',
    `/islands/${groupId}/wdrozenie/odhacz`,
    { krok, wlaczone },
    z.object({ krok: z.string(), odhaczone: z.boolean() }),
  );

/**
 * KTÓRY WĘZEŁ MA NAJBOGATSZĄ KONFIGURACJĘ — podpowiedź do zasiania mastera.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ TEN WYBÓR JEST NIEODWRACALNY. Odtworzenie bazy odmawia na nazwę, która już istnieje, więc
 *  drugiego zasiania nie będzie: poprawka wymaga nowej nazwy bazy i zmiany `SPS_DB` po stronie
 *  BillVisa. Zasianie z uboższej maszyny zapisuje jej ubytek jako stan wyjściowy CAŁEJ wyspy,
 *  a scalanie rozjechanych baz nie jest zbudowane (PLAN §35.12).
 *
 *  Zmierzone na testówce PESA: `DrawerCompartments` 1320 na jednej maszynie kontra 611 na dwóch
 *  pozostałych. Zasianie z którejś z tych dwóch skasowałoby połowę planogramu wyspy.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ LICZYMY SUMĘ WIERSZY ZE WSZYSTKICH ROZJAZDÓW, a nie z jednej tabeli. Rozjazd bywa w kilku
 * tabelach naraz (bęben to `Drawers` + `DrawerChannels` + `DrawerLids` + …), więc pojedyncza
 * tabela potrafi wskazać inny węzeł niż całość.
 */
export function najbogatszyWezel(rozjazdy: StanWdrozenia['rozjazdy']): { nodeId: string; wierszy: number } | null {
  const suma = new Map<string, number>();
  for (const r of rozjazdy) {
    for (const w of r.wezly) suma.set(w.nodeId, (suma.get(w.nodeId) ?? 0) + w.wierszy);
  }
  let najlepszy: { nodeId: string; wierszy: number } | null = null;
  for (const [nodeId, wierszy] of suma) {
    if (najlepszy === null || wierszy > najlepszy.wierszy) najlepszy = { nodeId, wierszy };
  }
  return najlepszy;
}

/**
 * NAZWA BAZY, POD KTÓRĄ WJEDZIE CHMUROWY MASTER.
 *
 * ⚠️ WOLNO TYLKO PRZED ZASIANIEM MASTERA — po nim API odmawia i odsyła do „Zmień bazę wyspy",
 * bo wtedy ta nazwa opisuje bazę, w której master naprawdę stoi. Uzasadnienie po stronie serwera
 * (`wdrozenie.service.ts`).
 */
export const nazwijBazeChmury = (groupId: string, database: string) =>
  apiSend('PUT', `/islands/${groupId}/wdrozenie/baza-chmury`, { database }, z.object({ masterDatabase: z.string() }));
